import { ArrowUpRight } from '@phosphor-icons/react';
import { site } from '../../content/site';
import type { FeaturedProject, ImageRef } from '../../content/site';
import { Shot } from './Shot';
import { VoiceDemo } from './VoiceDemo';
import { NgramPanel } from './NgramPanel';

/** In-page anchor of a project article (e.g. "projet-energie"). */
export const projectAnchor = (id: string) => `projet-${id}`;

/**
 * How much room an article takes in its group.
 * - lead: full width, the title on the left and the text on the right (the flagship, a site).
 * - card: one column, everything stacked, title first.
 * - compact: a short entry with no media (a tool still being prepared).
 */
export type ArticleSize = 'lead' | 'card' | 'compact';

const TEXT = 'text-graphite dark:text-mineral';
const MUTED = 'text-graphite-muted dark:text-dusk-muted';

/** Gap of the media compositions. */
const GAP = 'gap-[clamp(0.75rem,1.6vw,1.25rem)]';

const isPhone = (image: ImageRef) => image.width / image.height < 0.8;

/** Captures of a row of sites share one box, so they line up whatever the text above. */
const SITE_RATIO = '16 / 10';

/**
 * One project, told in the order a visitor reads it: header first (name, former name,
 * category and year, maturity, summary, stack, links), then its media, all inside the
 * same <article>. Nothing from another project can come between the two.
 */
export function ProjectArticle({
	project,
	size = 'card',
	pinMedia = false,
}: {
	project: FeaturedProject;
	size?: ArticleSize;
	/** In a row of articles: the article fills the row and its media sits at the bottom, aligned with its neighbours. */
	pinMedia?: boolean;
}) {
	const titleId = `${projectAnchor(project.id)}-titre`;
	const hasMedia =
		project.images.length > 0 || Boolean(project.videos?.length) || Boolean(project.ngram) || Boolean(project.channels?.length);

	return (
		<article id={projectAnchor(project.id)} aria-labelledby={titleId} className={pinMedia ? 'h-full' : undefined}>
			{/*
			 * The <article> is the scroll target (#projet-energie), so it never moves: the
			 * reveal animates this inner wrapper. Otherwise Lenis would measure an article
			 * still offset by its entrance transform and land it 40px off. The wrapper is
			 * also the size container of the recordings.
			 */}
			<div data-work-article className={`[container-type:inline-size] ${pinMedia ? 'flex h-full flex-col' : ''}`}>
				<ProjectHeader project={project} titleId={titleId} size={size} />
				{hasMedia && (
					<div className={`mt-[clamp(1.75rem,3.6vw,3rem)] ${pinMedia ? 'mt-auto pt-[clamp(1.75rem,3.6vw,3rem)]' : ''}`}>
						<ProjectMedia project={project} ratio={pinMedia ? SITE_RATIO : undefined} />
					</div>
				)}
			</div>
		</article>
	);
}

/** The media of a project, chosen by what its data contains. */
function ProjectMedia({ project, ratio }: { project: FeaturedProject; ratio?: string }) {
	if (project.videos?.length) return <VoiceDemo project={project} labels={site.work.videoLabels} />;
	if (project.ngram) return <NgramPanel project={project} labels={site.work.ngramLabels} />;
	if (project.images.length) {
		const phone = project.images.find(isPhone);
		if (phone) return <Flagship images={project.images} phone={phone} />;
		return <Gallery images={project.images} ratio={ratio} />;
	}
	if (project.channels?.length) return <Channels items={project.channels} />;
	return null;
}

/**
 * Volta. The captures are picked by shape, not by position in the data: the tallest is
 * the phone, the other one the dashboard. Neither takes the full width: the dashboard
 * stays at a moderate size and the small phone stands beside it, level with its bottom
 * edge (from 1024px). Below, one column: dashboard, then the phone set to the right.
 * The chart strip is gone: unreadable once shrunk, and the dashboard already shows it.
 */
function Flagship({ images, phone }: { images: ImageRef[]; phone: ImageRef }) {
	const lead = images.find((image) => image !== phone);
	return (
		<div
			className={`grid ${GAP} lg:grid-cols-[minmax(0,46rem)_clamp(9rem,15vw,12.5rem)] lg:items-end lg:justify-start lg:gap-x-[clamp(1.25rem,2.4vw,2rem)]`}
		>
			{lead && <Shot image={lead} sizes="(min-width: 1024px) 736px, 100vw" />}
			<Shot
				image={phone}
				screen="phone"
				sizes="(min-width: 1024px) 200px, 46vw"
				className="w-[46%] justify-self-end max-sm:w-[52%] lg:w-full lg:justify-self-stretch"
			/>
		</div>
	);
}

/** Any other project with screenshots: one after the other, at their natural ratio (or in a shared box). */
function Gallery({ images, ratio }: { images: ImageRef[]; ratio?: string }) {
	return (
		<div className={`grid ${GAP}`}>
			{images.map((image) => (
				<Shot
					key={image.src}
					image={image}
					screen={isPhone(image) ? 'phone' : 'desktop'}
					ratio={ratio}
					sizes="(min-width: 1024px) 33vw, 100vw"
				/>
			))}
		</div>
	);
}

/** Facts shown as small labels when a project has no screenshot (Orb). */
function Channels({ items }: { items: string[] }) {
	return (
		<ul className="flex flex-wrap gap-2">
			{items.map((item) => (
				<li
					key={item}
					className={`rounded-full border border-graphite/15 px-3.5 py-2 text-[0.875rem] leading-none dark:border-mineral/15 ${TEXT}`}
				>
					{item}
				</li>
			))}
		</ul>
	);
}

/**
 * Name, former name, category, year and maturity, then summary, stack and links.
 * `lead` sets the name on the left and the rest on the right once there is room.
 */
function ProjectHeader({ project, titleId, size }: { project: FeaturedProject; titleId: string; size: ArticleSize }) {
	const titleSize =
		size === 'compact'
			? 'text-[clamp(1.5rem,2.4vw,2rem)]'
			: size === 'lead'
				? 'text-[clamp(2.25rem,4.4vw,3.75rem)]'
				: 'text-[clamp(1.875rem,3.2vw,2.75rem)]';

	const identity = (
		<div>
			<h4
				id={titleId}
				className={`font-display font-bold leading-[1.02] tracking-[-0.03em] ${titleSize} ${TEXT} ${project.logo ? 'flex items-center gap-[0.3em]' : ''}`}
			>
				{project.logo && (
					<img
						src={project.logo}
						width={128}
						height={128}
						alt=""
						aria-hidden
						loading="lazy"
						decoding="async"
						className="block size-[0.95em] shrink-0 dark:invert"
					/>
				)}
				{project.title}
			</h4>
			{project.formerly && <p className={`mt-1.5 text-[0.875rem] ${MUTED}`}>{project.formerly}</p>}
			<p className={`mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 font-mono text-[0.8125rem] ${MUTED}`}>
				<span>
					{project.kind} · {project.year}
				</span>
				{project.status && (
					<span
						className={`rounded-full border border-graphite/25 px-2.5 py-1 font-sans text-[0.75rem] font-medium leading-none dark:border-mineral/25 ${TEXT}`}
					>
						{project.status}
					</span>
				)}
			</p>
		</div>
	);

	const details = (
		<div>
			<p className={`max-w-prose text-[1.0625rem] leading-relaxed ${MUTED}`}>{project.summary}</p>
			{project.stack.length > 0 && (
				<ul className="mt-5 flex flex-wrap gap-2">
					{project.stack.map((tech) => (
						<li
							key={tech}
							className={`rounded-full bg-mist px-3 py-1.5 font-mono text-[0.75rem] leading-none dark:bg-forest-deep ${TEXT}`}
						>
							{tech}
						</li>
					))}
				</ul>
			)}
			{project.links.length > 0 && (
				<ul className="mt-3 flex flex-wrap gap-x-6">
					{project.links.map((link) => (
						<li key={link.href}>
							<a
								href={link.href}
								target="_blank"
								rel="noopener noreferrer"
								aria-describedby={titleId}
								className={`group/link inline-flex min-h-11 items-center gap-1.5 text-[0.9375rem] font-medium underline decoration-graphite/25 decoration-1 underline-offset-[6px] transition-[text-decoration-color] duration-300 hover:decoration-graphite dark:decoration-mineral/30 dark:hover:decoration-mineral ${TEXT}`}
							>
								{link.label}
								<ArrowUpRight
									aria-hidden
									size={16}
									weight="bold"
									className="transition-transform duration-300 ease-expo group-hover/link:-translate-y-0.5 group-hover/link:translate-x-0.5"
								/>
							</a>
						</li>
					))}
				</ul>
			)}
		</div>
	);

	if (size === 'lead') {
		return (
			<header className="grid gap-y-5 lg:grid-cols-12 lg:gap-x-10">
				<div className="lg:col-span-5">{identity}</div>
				<div className="lg:col-span-7 lg:pt-2">{details}</div>
			</header>
		);
	}

	return (
		<header className="grid gap-y-5">
			{identity}
			{details}
		</header>
	);
}
